import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { LayoutModule } from '@control-tower/lib';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule, appInitializer } from './core/core.module';
import { AppConfigService } from './core/app-config.service';
import { AddHeaderInterceptor } from './core/add-header.interceptor';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { OrderModule } from 'ngx-order-pipe';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { AppMenuService } from './core/app-menu.service';
import { UserService } from './core/user.service';
import { ModalModule } from 'ngx-bootstrap';
import { SpinnerHttpModule, SpinnerModule } from '@control-tower/lib';
import { SharedModule } from './shared/shared.module';
import { FormlyModule, FORMLY_CONFIG } from '@ngx-formly/core';
import { MatNativeDateModule } from '@angular/material/core';
import { WidgetsModule } from './components/widgets/widgets.module';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { Logger } from './core/logger';
import { LoggerFactory } from './core/logger-factory';
import { WidgetsService } from './services/widgets/widgets.service';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { CommandService } from './services/command/command.service';
import { CommandSectionService } from './services/command/command-section.service';
import { CommandQuestionService } from './services/command/command-question.service';
import { CommandRowService } from './services/command/command-row.service';
import { CommandArrayService } from './services/command/command-array.service';
import { CommandFieldService } from './services/command/command-field.service';
import { CommandTextService } from './services/command/command-text.service';
import { RenderService } from './services/render/render.service';
import { CommandColumnService } from './services/command/command-column.service';
import { CloneService } from './services/clone/clone.service';
import { CloneFormService } from './services/clone/clone-form.service';
import { CloneQuestionService } from './services/clone/clone-question.service';
import { RenderFormService } from './services/render/render-form.service';
import { RenderQuestionService } from './services/render/render-question.service';
import { RenderSectionService } from './services/render/render-section.service';
import { FormlyValidationService } from './services/formly-validation/formly-validation.service';
import { VrskFormlyRepeatTypeComponent } from './components/formly/types/vrsk-formly-repeat-type/vrsk-formly-repeat-type.component';
import { VrskFormlyFlexLayoutComponent } from './components/formly/types/vrsk-formly-flex-layout/vrsk-formly-flex-layout.component';
import { VrskFormlyModule } from './components/formly/widgets-formly.module';
import { WidgetsQuestionWrapperComponent } from './components/formly/wrappers/widgets-question-wrapper.component';
import { WidgetsSectionWrapperComponent } from './components/formly/wrappers/widgets-section-wrapper.component';
import { WidgetsFieldWrapperComponent } from './components/formly/wrappers/widgets-field-wrapper.component';
import { WidgetsMetadataService } from './services/widgets/widgets-metadata.service';
import { RenderRowService } from './services/render/render-row.service';
import { RenderColumnService } from './services/render/render-column.service';
import { CommandRadioService } from './services/command/command-radio.service';
import { CommandInputService } from './services/command/command-input.service';
import { CloneSectionService } from './services/clone/clone-section.service';
import { CloneColumnService } from './services/clone/clone-column.service';
import { CloneRowService } from './services/clone/clone-row.service';
import { FormService } from './services/api/form.service';
import { updateStatusService } from './services/api/update-status-service';
import { QuestionPreviewService } from './services/question-preview.service';
import { CommandCreateForm } from './services/command/command-create-form.service';
import { CommandUpdateNodeService } from './services/command/command-update-node.service';
import { CommandResequenceSectionsService } from './services/command/command-resequence-sections.service';
import { CommandResequenceQuestionsService } from './services/command/command-resequence-questions.service';
import { CommandDeleteNodeService } from './services/command/command-delete-node.service';
import { CommandCheckboxService } from './services/command/command-checkbox.service';
import { CommandTimeRangeService } from './services/command/command-time-range.service';
import { CommandRealtedInputService } from './services/command/command-related-input.service';
import { QuestionService } from './services/api/question.service';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { minCurrentYearValidator } from './validators/min-current-year.validator';
import { maxCurrentYearValidator } from './validators/max-current-year.validator';
import { oneOptionSelectedValidator } from './validators/one-option-selected.validator';
import { oneIndicatorCheckedValidator } from './validators/one-indicator-checked.validator';
import { oneInputFilledValidator } from './validators/one-input-filled.validator';
import { oneInputNotEmptyValidator } from './validators/one-input-not-empty.validator';
import { requiredIfFieldsValidator } from './validators/required-if-fields.validator';
import { maxRelatedInputValidator } from './validators/max-related-input.validator';
import { arrayRegexValidator } from './validators/array-regex.validator';
import { ipValidator } from './validators/ip.validator';
import { sumValidator } from './validators/sum.validator';
import { trimSurroundValidator } from './validators/trim-surround.validator';
import { pastYearValidator } from './validators/past-year.validator';
import { timeRangeValidator } from './validators/time-range.validator';
import { decimalValidator } from './validators/decimal.validator';
import { decimalMaxValidator } from './validators/decimalmax.validator';
import { dateFormatValidator } from './validators/dateFormat.validator';
import { monthFormatValidator } from './validators/monthFormat.validator';
import { percentageValidator } from './validators/percentage.validator';
import { VrskAuthOktaModule } from '@uw-verisk/auth-okta';
import { FormlyFieldInput } from '@ngx-formly/material/input';
import { FormlyFieldSelect } from '@ngx-formly/material/select';
import { FormlyFieldRadio } from '@ngx-formly/material/radio';
import { FormlyFieldCheckbox } from '@ngx-formly/material/checkbox';
import { FormlyFieldMultiCheckbox } from '@ngx-formly/material/multicheckbox';
import { FormlyFieldButton } from './components/formly/types/button-type.component';
import { totalCalculator } from './validators/total.validator';
import { sumTotalValidator } from './validators/sumTotal.validator';
import { ErrorHandlerService } from './services/formly-error-handler.service';
import { enforceAlphabeticValidator } from './validators/enforce-alphabetic.validator';
import { enforceNumericValidator } from './validators/enforce-numeric.validator';
import { keyOverrideService } from './services/workflow/keyOverrideService.service';
import { phoneNumberValidator } from './validators/phoneNumber-validator/phoneNumber.validator';
import { totalAddendCalculator } from './validators/total-addend.validator';
import { productCalculator } from './validators/multiplication.validator';
import { AddKongInterceptor } from './core/add-kong-interceptor';




// TODO: Move to files in /validators folder
export const minValidationMessage = (err, field) => `This value should be more than or equal to ${field.props.min}`;

export const maxValidationMessage = (err, field) => `This value should be less than or equal to ${field.props.max}`;

const appearance: MatFormFieldDefaultOptions = {
  // appearance: 'outline'
  appearance: 'legacy'
};

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    CoreModule,
    LayoutModule,
    TooltipModule.forRoot(),
    NgMultiSelectDropDownModule.forRoot(),
    OrderModule,
    // required animations module
    BrowserAnimationsModule,
    // ToastrModule added
    ToastrModule.forRoot({
      timeOut: 3000,
      progressBar: true,
      closeButton: true
    }),
    ModalModule.forRoot(),
    SpinnerModule,
    SpinnerHttpModule,
    SharedModule,
    // Formly Modules
    MatNativeDateModule,
    VrskFormlyModule,
    FormlyModule,
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    WidgetsModule,
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'max', message: maxValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'required', message: 'This field is required' }
      ],
      types: [{ name: 'button', component: FormlyFieldButton}]
    }),
   
    VrskAuthOktaModule,
  ],
  declarations: [AppComponent],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: appearance
    },
    { provide: HTTP_INTERCEPTORS, useClass: AddHeaderInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [AppMenuService, AppConfigService, UserService],
      multi: true
    },
    {
      provide: FORMLY_CONFIG,
      useValue: {
        // Standard validators: Packaged with Angular SPA
        validators: [
          { name: 'requiredIfFields', validation: requiredIfFieldsValidator },
          { name: 'ip', validation: ipValidator },
          { name: 'sum', validation: sumValidator },
          { name: 'pastYear', validation: pastYearValidator },
          { name: 'trimValidation', validation: trimSurroundValidator },
          { name: 'minCurrentYear', validation: minCurrentYearValidator },
          { name: 'maxCurrentYear', validation: maxCurrentYearValidator },
          { name: 'oneOptionSelected', validation: oneOptionSelectedValidator },
          { name: 'oneIndicatorChecked', validation: oneIndicatorCheckedValidator },
          { name: 'oneInputFilled', validation: oneInputFilledValidator},
          { name: 'oneInputNotEmpty', validation: oneInputNotEmptyValidator },
          { name: 'timeRangeValidation', validation: timeRangeValidator },
          { name: 'mindecimalValidator', validation: decimalValidator },
          { name: 'maxdecimalValidator', validation: decimalMaxValidator },
          { name: 'relatedInputValidation', validation: maxRelatedInputValidator},
          { name: 'arrayRegexValidation', validation: arrayRegexValidator},
          { name: 'dateFormat', validation: dateFormatValidator}, 
          { name: 'monthFormat', validation: monthFormatValidator },
          { name: 'percentage', validation: percentageValidator},
          { name: 'sumTotalValidation', validation: sumTotalValidator },
          { name: 'enforceNumeric', validation: enforceNumericValidator },
          { name: 'enforceAlphabetic', validation: enforceAlphabeticValidator },
          { name: 'phoneNumber', validation: phoneNumberValidator },

          // We call validators that calculate fields "Calculators"
          { name: 'total', validation: totalCalculator },
          { name: 'totalAddend', validation: totalAddendCalculator },
          { name: 'product', validation: productCalculator },

        ],
        validationMessages: [
          { name: 'requiredIfFields', message: "Error in field" },
          { name: 'ip', message: "Error in IP address" },
          { name: 'sum', message: 'Something went wrong' },
        ],
        types: [
          { name: 'button', component: FormlyFieldButton },  
          { name: 'repeat', component: VrskFormlyRepeatTypeComponent },
          { name: 'flex-layout', component: VrskFormlyFlexLayoutComponent },
          { name: 'uw-input', component: FormlyFieldInput, wrappers: ['form-field'] },
          { name: 'uw-select', component: FormlyFieldSelect, wrappers: ['form-field'] },         
          { name: 'uw-radio', component: FormlyFieldRadio, wrappers: ['form-field'] },
          { name: 'uw-checkbox', component: FormlyFieldCheckbox, wrappers: ['form-field'] },
          { name: 'uw-multicheckbox', component: FormlyFieldMultiCheckbox, wrappers: ['form-field'] }, 
          { name: 'formly-group', defaultOptions: { defaultValue: {} } }
        ],
        wrappers: [
          { name: 'question', component: WidgetsQuestionWrapperComponent, wrappers: ['question'] },
          { name: 'section', component: WidgetsSectionWrapperComponent, wrappers: ['section'] },
          { name: 'field', component: WidgetsFieldWrapperComponent, wrappers: ['field'] }          
        ]
      },
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AddKongInterceptor, multi: true },  
    { provide: Logger, useFactory: LoggerFactory },
    { provide: ErrorHandler, useClass: ErrorHandlerService },

    WidgetsService,
    CloneService,
    WidgetsMetadataService,
    CloneFormService,
    CloneSectionService,
    CloneQuestionService,
    CloneRowService,
    CloneColumnService,
    RenderService,
    RenderFormService,
    RenderSectionService,
    RenderQuestionService,
    RenderRowService,
    RenderColumnService,
    CommandService,
    CommandSectionService,
    CommandQuestionService,
    CommandRowService,
    CommandArrayService,
    CommandFieldService,
    CommandRadioService,
    CommandTimeRangeService,
    CommandInputService,
    CommandTextService,
    CommandColumnService,
    CommandCreateForm,
    CommandUpdateNodeService,
    CommandResequenceSectionsService,
    CommandResequenceQuestionsService,
    CommandDeleteNodeService,
    FormlyValidationService,
    FormService,
    CommandCheckboxService,
    CommandRealtedInputService,
    QuestionService,
    updateStatusService,
    QuestionPreviewService,
    keyOverrideService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
